import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useActiveSession } from '../contexts/ActiveSessionContext';
import { useVelaTheme } from '../contexts/VelaThemeContext';
import { saveEncrypted, getAllEncrypted, getAppState, setAppState, STORES } from '../utils/storage';
import axios from 'axios';
import { PaperPlaneRight, Lock, LockOpen, FloppyDisk, SignOut, BookOpen, ShieldCheck, Gear, X, Sun, Moon } from 'phosphor-react';
import { toast } from 'sonner';
import SupportCircleOnboarding from '../components/SupportCircleOnboarding';
import OnboardingFlow from '../components/OnboardingFlow';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Safely strip common markdown formatting from Vela's responses so it doesn't
// display as raw characters. We preserve the underlying text — only the
// formatting markers are removed. Applied to assistant messages only.
const stripMarkdown = (text) => {
  if (!text) return '';
  return text
    // Bold: **text** or __text__
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/__(.+?)__/g, '$1')
    // Italic: *text* or _text_ (avoid matching inside words)
    .replace(/(^|[\s(])\*([^*\n]+?)\*(?=[\s.,!?)]|$)/g, '$1$2')
    .replace(/(^|[\s(])_([^_\n]+?)_(?=[\s.,!?)]|$)/g, '$1$2')
    // Inline code: `text`
    .replace(/`([^`\n]+)`/g, '$1')
    // Headers: leading # symbols
    .replace(/^#{1,6}\s+/gm, '')
    // List markers at line start: -, *, +
    .replace(/^[\s]*[-*+]\s+/gm, '')
    // Blockquote markers
    .replace(/^>\s+/gm, '');
};

const Chat = () => {
  // Conversation state is held in ActiveSessionContext so it survives
  // navigation between authenticated routes (e.g. Chat <-> Support).
  // It still lives in memory only — never auto-persisted.
  const {
    messages,
    setMessages,
    sessionSaved,
    setSessionSaved,
    input,
    setInput,
    isLoading,
    setIsLoading,
    sessionId,
    sessionStartTime,
    sessionIdRef,
  } = useActiveSession();

  const [isSaving, setIsSaving] = useState(false);
  const [showSupportOnboarding, setShowSupportOnboarding] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [hasExistingSessions, setHasExistingSessions] = useState(false);
  const messagesEndRef = useRef(null);
  const { encryptionKey, logout } = useAuth();
  const { theme: velaTheme, toggleTheme: toggleVelaTheme } = useVelaTheme();
  const navigate = useNavigate();

  useEffect(() => {
    initializeChat();
    // No auto-save on unmount. Nothing persists unless user taps Save to Diary.
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const initializeChat = async () => {
    if (!encryptionKey) return;

    try {
      // Check for existing sessions
      const sessions = await getAllEncrypted(STORES.SESSIONS, encryptionKey).catch(() => []);
      setHasExistingSessions(sessions.length > 0);

      // Check onboarding
      const onboardingCompleted = await getAppState('onboarding_completed');
      if (!onboardingCompleted) {
        setShowOnboarding(true);
      } else {
        // Check support circle onboarding
        const supportOnboardingCompleted = await getAppState('support_onboarding_completed');
        if (!supportOnboardingCompleted) {
          setShowSupportOnboarding(true);
        }
      }
    } catch (error) {
      console.error('Initialize chat error:', error);
    }
  };

  const handleOnboardingComplete = async () => {
    await setAppState('onboarding_completed', true);
    setShowOnboarding(false);
    
    // Show support onboarding next
    const supportOnboardingCompleted = await getAppState('support_onboarding_completed');
    if (!supportOnboardingCompleted) {
      setTimeout(() => setShowSupportOnboarding(true), 500);
    }
  };

  const handleSupportOnboardingComplete = async () => {
    await setAppState('support_onboarding_completed', true);
    setShowSupportOnboarding(false);
  };

  const handleSupportOnboardingSkip = async () => {
    await setAppState('support_onboarding_completed', true);
    setShowSupportOnboarding(false);
  };

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: input.trim(),
      timestamp: new Date().toISOString(),
    };

    const currentInput = input.trim();
    setInput('');

    // Snapshot the session identity at request time. If the user logs out
    // (which generates a new sessionId via ActiveSessionContext) or the
    // session is otherwise discarded before the response arrives, we drop
    // the response rather than repopulate a cleared/foreign session.
    const requestSessionId = sessionId;

    // Functional updater avoids stale closures when the user sends a
    // second message before the first response arrives.
    let historyForRequest = [];
    setMessages((prev) => {
      historyForRequest = [...prev, userMessage];
      return historyForRequest;
    });
    setSessionSaved(false); // any new activity invalidates prior save state
    setIsLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/chat`, {
        text: currentInput,
        session_id: requestSessionId,
        message_history: historyForRequest,
      });

      // Session identity guard: if the session was discarded / user logged
      // out while this request was in flight, drop the response.
      if (requestSessionId !== sessionIdRef.current) {
        return;
      }

      const velaMessage = {
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date().toISOString(),
      };

      // Functional updater: append to whatever the current messages are,
      // so a response arriving late does not clobber newer user messages.
      setMessages((prev) => [...prev, velaMessage]);

      // No auto-persist. Interaction metadata is only saved as part of an
      // explicit Save to Diary action. Follow-up events (no content) are
      // created only when the user has enabled Auto Check-In Alerts.
      await checkFollowUpThreshold(userMessage);

    } catch (error) {
      // Only surface the error if the session is still the same one that
      // issued the request. Otherwise it belongs to a cleared session.
      if (requestSessionId !== sessionIdRef.current) return;
      console.error('Chat error:', error);
      toast.error('Failed to send message');
    } finally {
      // Only reset loading if we're still the same session; otherwise the
      // discard/logout flow already cleared isLoading.
      if (requestSessionId === sessionIdRef.current) {
        setIsLoading(false);
      }
    }
  };

  const saveCurrentSession = async () => {
    if (!encryptionKey || messages.length === 0) return;

    setIsSaving(true);
    try {
      // Generate enhanced summary with metadata as part of the explicit save.
      // Summary/metadata are ONLY created here, never automatically.
      const summaryResult = await generateSessionSummary();
      
      const session = {
        id: sessionId,
        startTime: sessionStartTime,
        endTime: new Date().toISOString(),
        messages: messages,
        messageCount: messages.length,
        summary: summaryResult.title,
        metadata: summaryResult.metadata,
        savedAt: new Date().toISOString(),
      };

      await saveEncrypted(STORES.SESSIONS, session, encryptionKey);
      setSessionSaved(true);
      toast.success('Saved to your diary', {
        icon: <Lock size={16} weight="duotone" />,
      });
    } catch (error) {
      console.error('Save session error:', error);
      toast.error('Could not save to diary');
    } finally {
      setIsSaving(false);
    }
  };

  const generateSessionSummary = async () => {
    if (messages.length === 0) return {
      title: 'Empty session',
      metadata: { tone: 'calm', themes: [], significance: 'low' }
    };

    try {
      // Prepare full session context for analysis
      const conversationContext = messages.map((msg, idx) => {
        const role = msg.role === 'user' ? 'User' : 'Vela';
        return `${role}: ${msg.content}`;
      }).join('\n\n');

      // Limit context to reasonable size (last 2000 chars if very long)
      const contextToAnalyze = conversationContext.length > 2000 
        ? '...' + conversationContext.slice(-2000) 
        : conversationContext;

      // Enhanced summary prompt
      const summaryPrompt = `Analyze this full conversation session and create a summary.

CONVERSATION:
${contextToAnalyze}

Generate a response in this exact JSON format:
{
  "title": "A warm, human one-sentence diary title that captures the emotional or practical core of this session (max 60 chars)",
  "tone": "one of: calm, mixed, distressed, activated, reflective, hopeful",
  "themes": ["short", "descriptive", "tags"],
  "significance": "low, medium, or high based on emotional intensity and meaning"
}

Rules:
- Title should be compassionate and natural, NOT clinical
- Reflect the overall emotional arc, not just opening messages
- Use emotional and relational meaning
- Avoid generic phrases like "The user talked about feelings"
- Themes should be 1-2 words each, max 4 themes
- Return ONLY the JSON, no other text`;

      const response = await axios.post(`${BACKEND_URL}/api/chat`, {
        text: summaryPrompt,
        session_id: `summary_${sessionId}`,
        message_history: [],
      });

      // Parse GPT response
      try {
        // Try to extract JSON from response
        const responseText = response.data.response;
        const jsonMatch = responseText.match(/\{[\s\S]*\}/);
        
        if (jsonMatch) {
          const parsed = JSON.parse(jsonMatch[0]);
          
          // Validate and clean the response
          const validTones = ['calm', 'mixed', 'distressed', 'activated', 'reflective', 'hopeful'];
          const validSignificance = ['low', 'medium', 'high'];
          
          return {
            title: parsed.title?.substring(0, 100) || getFallbackTitle(),
            metadata: {
              tone: validTones.includes(parsed.tone) ? parsed.tone : 'mixed',
              themes: Array.isArray(parsed.themes) ? parsed.themes.slice(0, 4) : [],
              significance: validSignificance.includes(parsed.significance) ? parsed.significance : 'medium'
            }
          };
        }
      } catch (parseError) {
        console.error('Failed to parse summary JSON:', parseError);
      }

      // Fallback if parsing fails
      return getFallbackSummary();

    } catch (error) {
      console.error('Summary generation error:', error);
      return getFallbackSummary();
    }
  };

  const getFallbackTitle = () => {
    // Create intelligent fallback from actual content
    const userMessages = messages.filter(m => m.role === 'user');
    if (userMessages.length === 0) return 'A moment of reflection';

    // Try to extract meaningful content from first substantial message
    const substantialMsg = userMessages.find(m => m.content.length > 20) || userMessages[0];
    const firstSentence = substantialMsg.content.split(/[.!?]/)[0].trim();
    
    // Clean and truncate
    const cleaned = firstSentence
      .replace(/^(I|i) (feel|felt|am|was|think|thought)/, '')
      .trim();
    
    return cleaned.length > 10 && cleaned.length < 60 
      ? cleaned.charAt(0).toUpperCase() + cleaned.slice(1)
      : substantialMsg.content.substring(0, 60) + (substantialMsg.content.length > 60 ? '...' : '');
  };

  const getFallbackSummary = () => {
    // Analyze message content for fallback metadata
    const userMessages = messages.filter(m => m.role === 'user');
    const allUserText = userMessages.map(m => m.content.toLowerCase()).join(' ');

    // Simple keyword analysis for tone
    const distressWords = ['panic', 'crisis', 'overwhelm', 'scared', 'terrified', 'desperate'];
    const calmWords = ['better', 'calm', 'okay', 'peaceful', 'settled'];
    const reflectiveWords = ['thinking', 'remember', 'understand', 'realize', 'notice'];

    let tone = 'mixed';
    if (distressWords.some(w => allUserText.includes(w))) tone = 'distressed';
    else if (calmWords.some(w => allUserText.includes(w))) tone = 'calm';
    else if (reflectiveWords.some(w => allUserText.includes(w))) tone = 'reflective';

    // Extract simple themes from content
    const themeMap = {
      'overwhelm': ['overwhelm', 'too much', 'stressed'],
      'support': ['support', 'help', 'need'],
      'memory': ['remember', 'past', 'memory'],
      'planning': ['plan', 'tomorrow', 'next', 'will'],
      'anxiety': ['anxiety', 'anxious', 'worry', 'worried'],
      'relationships': ['friend', 'family', 'partner', 'relationship']
    };

    const themes = Object.keys(themeMap).filter(theme =>
      themeMap[theme].some(word => allUserText.includes(word))
    ).slice(0, 4);

    // Determine significance
    const hasHighIntensity = distressWords.some(w => allUserText.includes(w));
    const significance = hasHighIntensity ? 'high' : userMessages.length > 5 ? 'medium' : 'low';

    return {
      title: getFallbackTitle(),
      metadata: {
        tone,
        themes: themes.length > 0 ? themes : ['reflection'],
        significance
      }
    };
  };

  const checkFollowUpThreshold = async (userMsg) => {
    if (!encryptionKey) return;

    try {
      // Gate on explicit user consent: Auto Check-In Alerts must be enabled.
      // If not enabled, do NOT create any follow-up event at all.
      const autoAlertsEnabled = await getAppState('auto_checkin_alerts');
      if (!autoAlertsEnabled) return;

      const followUpKeywords = ['panic', 'crisis', 'overwhelm', 'can\'t', 'help', 'desperate', 'alone'];
      const hasFollowUpTrigger = followUpKeywords.some(word => 
        userMsg.content.toLowerCase().includes(word)
      );

      if (hasFollowUpTrigger) {
        // Store ONLY minimum non-content operational state.
        // No message text, no triggerMessage, no content snippet — permanently.
        const followUpEvent = {
          id: Date.now(),
          timestamp: new Date().toISOString(),
          intensity: 'high',
          status: 'pending',
        };

        await saveEncrypted(STORES.FOLLOW_UPS, followUpEvent, encryptionKey);
        
        toast('Vela will check in with you later', {
          description: 'A gentle follow-up will be sent after this conversation',
          duration: 5000,
        });
      }
    } catch (error) {
      console.error('Failed to create follow-up event:', error);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleLogout = () => {
    // Nothing is saved unless the user chose Save to Diary.
    // Any unsaved conversation is discarded from memory here.
    logout();
    navigate('/landing');
  };

  const getGreeting = () => {
    if (hasExistingSessions) {
      const hour = new Date().getHours();
      const timeGreeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
      return `${timeGreeting}. I'm here whenever you need.`;
    }
    return "Hello. This is your private space with me. Share what's on your mind—nothing is saved unless you choose to.";
  };

  return (
    <div className="min-h-screen bg-stone-50 noise-texture flex flex-col">
      {showOnboarding && (
        <OnboardingFlow onComplete={handleOnboardingComplete} />
      )}

      {showSupportOnboarding && !showOnboarding && (
        <SupportCircleOnboarding
          onComplete={handleSupportOnboardingComplete}
          onSkip={handleSupportOnboardingSkip}
        />
      )}
      
      {/* Header */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src={velaTheme === 'dark' ? '/aravela-logo-dark.png' : '/aravela-logo-light.png'}
              alt="Vela"
              className="w-10 h-10 object-contain rounded-full"
              data-testid="chat-header-logo"
            />
            <div>
              <h1 className="font-serif font-semibold text-lg text-stone-800" data-testid="chat-title">Vela</h1>
              <p className="text-xs text-stone-500">Your companion</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={toggleVelaTheme}
              className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
              data-testid="vela-chat-theme-toggle"
              title={velaTheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              aria-label="Toggle theme"
            >
              {velaTheme === 'dark' ? <Sun size={20} weight="duotone" /> : <Moon size={20} weight="duotone" />}
            </button>
            <button
              onClick={() => navigate('/diary')}
              className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
              data-testid="nav-diary-btn"
              title="Diary"
            >
              <BookOpen size={20} weight="duotone" />
            </button>
            <button
              onClick={() => navigate('/safety')}
              className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
              data-testid="nav-safety-btn"
              title="Safety"
            >
              <ShieldCheck size={20} weight="duotone" />
            </button>
            <button
              onClick={() => navigate('/support')}
              className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
              data-testid="nav-support-btn"
              title="Support AraVela"
            >
              <Gear size={20} weight="duotone" />
            </button>
            <button
              onClick={handleLogout}
              className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
              data-testid="logout-btn"
              title="Logout"
            >
              <SignOut size={20} weight="duotone" />
            </button>
          </div>
        </div>
      </header>

      {/* Chat Messages */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-4 py-8">
          {messages.length === 0 && (
            <div className="text-center py-16" data-testid="empty-chat-state">
              <div className="mb-4 flex justify-center">
                <img
                  src={velaTheme === 'dark' ? '/aravela-logo-dark.png' : '/aravela-logo-light.png'}
                  alt="AraVela"
                  className="w-24 h-24 object-contain rounded-full"
                  data-testid="chat-empty-logo"
                />
              </div>
              <h2 className="text-2xl font-serif font-semibold text-stone-800 mb-2">
                {getGreeting()}
              </h2>
              {!hasExistingSessions && (
                <p className="text-stone-600 max-w-md mx-auto">
                  Nothing is saved unless you choose to. Tap Save to Diary when you'd like to keep this session.
                </p>
              )}
            </div>
          )}

          <div className="space-y-4">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                data-testid={`message-${msg.role}-${idx}`}
              >
                <div
                  className={`max-w-[80%] rounded-2xl px-5 py-3 ${
                    msg.role === 'user'
                      ? 'bg-primary/10 text-stone-800'
                      : 'bg-secondary/10 text-stone-800'
                  }`}
                >
                  <p className="whitespace-pre-wrap leading-relaxed">
                    {msg.role === 'assistant' ? stripMarkdown(msg.content) : msg.content}
                  </p>
                  <span className="text-xs text-stone-400 mt-1 block">
                    {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              </div>
            ))}

            {isLoading && (
              <div className="flex justify-start" data-testid="loading-indicator">
                <div className="bg-secondary/10 rounded-2xl px-5 py-3">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 rounded-full bg-secondary animate-pulse" style={{ animationDelay: '0s' }} />
                    <div className="w-2 h-2 rounded-full bg-secondary animate-pulse" style={{ animationDelay: '0.2s' }} />
                    <div className="w-2 h-2 rounded-full bg-secondary animate-pulse" style={{ animationDelay: '0.4s' }} />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input Area */}
      <div className="bg-white border-t border-stone-200 sticky bottom-0">
        <div className="max-w-4xl mx-auto px-4 py-4">
          {messages.length > 0 && (
            <div className="mb-3 flex items-center justify-between text-sm gap-3 flex-wrap">
              <span className="text-stone-500 flex items-center gap-2" data-testid="save-status">
                {sessionSaved ? (
                  <>
                    <Lock size={16} weight="duotone" className="text-primary" />
                    Saved to your diary
                  </>
                ) : (
                  <>
                    <LockOpen size={16} weight="duotone" />
                    Nothing is saved unless you choose to
                  </>
                )}
              </span>
              <button
                onClick={saveCurrentSession}
                disabled={isSaving || isLoading}
                className="flex items-center gap-2 bg-primary/10 text-primary rounded-full px-4 py-2 text-sm hover:bg-primary/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="save-to-diary-btn"
              >
                <FloppyDisk size={16} weight="duotone" />
                {isSaving ? 'Saving…' : sessionSaved ? 'Update diary entry' : 'Save to Diary'}
              </button>
            </div>
          )}

          <div className="flex gap-2">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Share what's on your mind..."
              className="flex-1 bg-stone-50 border-transparent focus:border-primary/50 focus:ring-2 focus:ring-primary/20 rounded-xl px-4 py-3 transition-all resize-none"
              rows="2"
              data-testid="chat-input"
            />
            <button
              onClick={sendMessage}
              disabled={!input.trim() || isLoading}
              className="bg-primary text-white rounded-xl px-6 hover:shadow-soft transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              data-testid="send-message-btn"
            >
              <PaperPlaneRight size={20} weight="duotone" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Chat;
